import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@clerk/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSearchParams } from 'react-router-dom';
import {
  isYoutubeUrl,
  extractUrlsFromText,
  extractYoutubeUrlsFromText,
} from '../../Homepage/Utils/GenerationUtils';
import { cleanBookTitle, makeBookId } from '../../Homepage/Contexts/DiffitBooksContext';
import { getFollowUpQuestions } from '../../../services/UnlimitedService';
import { Mixpanel } from '../../../services/Mixpanel';
import { trackMetaCustomEvent } from '../../../services/MetaPixel';
import { trackChatGPTCustomEvent } from '../../../services/ChatGPTPixel';
import { MAX_URLS } from '../constants';
import { useSignInModal } from '../../../contexts/SignInModalContext';
import { followUpAlignmentContext } from '../../../utils/curriculum';
import { isAnonCreateLimit } from '../../../utils/isRateLimited';
import { useTutorialContext } from '../../Base/Context/TutorialContext';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';
import { useError } from '../../../contexts/ErrorContext';
import { isProjectQuotaExhausted } from '../../../utils/isProjectQuotaExceeded';

// ── Pure helpers (no React, easy to test) ────────────────────────────

function checkUploadsReady(attachments) {
  if (attachments.pdfFile && !attachments.uploadedPdfURL) {
    return 'Please wait for the PDF to finish uploading.';
  }
  if (
    attachments.imageFiles.length > 0 &&
    attachments.uploadedImageURLs.length < attachments.imageFiles.length
  ) {
    return 'Please wait for images to finish uploading.';
  }
  return null;
}

function findFirstYoutubeUrl(query, attachedLinks) {
  const inText = extractYoutubeUrlsFromText(query);
  const inLinks = attachedLinks.filter((url) => isYoutubeUrl(url));
  return inText[0] || inLinks[0] || null;
}

function extractAndValidateUrls(query, attachedLinks) {
  const urls = [...new Set([...extractUrlsFromText(query), ...attachedLinks])];
  if (urls.length > MAX_URLS) {
    return {
      urls,
      error: `Too many links. Please include ${MAX_URLS} or fewer links in your request.`,
    };
  }
  return { urls, error: null };
}

function detectBookMatch(query, findPossibleBookMatchForTopic) {
  if (!findPossibleBookMatchForTopic) return null;

  const match = findPossibleBookMatchForTopic(query);
  if (!match) return null;

  Mixpanel.track('Unlimited Book Match Detected', {
    book_title: match.title,
    book_author: match.author,
    query,
  });

  return {
    book_id: makeBookId(match),
    title: match.title,
    author: match.author,
    chapters: match.chapters.map((ch, idx) =>
      typeof ch === 'object' ? { title: ch.title, index: idx } : { title: ch, index: idx }
    ),
    cover: match.cover,
  };
}

function buildSelectedBook(selectedBook, startChapter, endChapter) {
  if (!selectedBook) return undefined;
  return {
    book_id: makeBookId(selectedBook),
    title: cleanBookTitle(selectedBook.title),
    author: selectedBook.author,
    start_chapter_idx: startChapter,
    end_chapter_idx: endChapter,
    chapter_names: selectedBook.chapters
      .slice(startChapter, endChapter + 1)
      .map((ch) => (typeof ch === 'object' ? ch.title : ch)),
  };
}

function buildContext({
  selectedTemplate,
  languageStandards,
  contentStandards,
  curriculumSelection,
  selectedReadingLevels,
  selectedLanguages,
}) {
  return {
    ...(selectedTemplate && {
      template_title: selectedTemplate.title,
      template_description: selectedTemplate.description,
    }),
    ...followUpAlignmentContext({ languageStandards, contentStandards, curriculumSelection }),
    ...(selectedReadingLevels.length > 0 && {
      reading_level_id: selectedReadingLevels[0].id,
      reading_level_name: selectedReadingLevels[0].name,
    }),
    ...(selectedLanguages.length > 0 && { language: selectedLanguages[0].name }),
  };
}

// ── Hook ─────────────────────────────────────────────────────────────

/**
 * Handles submit validation (auth gating, upload checks, YouTube detection,
 * URL extraction, book matching) and the initial getFollowUpQuestions API call.
 * Delegates to the follow-up flow on success.
 *
 * @param {Object} params
 * @param {string} params.query - Current query text.
 * @param {Function} params.setError - Setter to display error messages.
 * @param {Object} params.attachments - Attachments hook return value.
 * @param {Object|null} params.selectedBook - Currently selected book.
 * @param {number} params.startChapter - Start chapter index.
 * @param {number} params.endChapter - End chapter index.
 * @param {Object|null} params.selectedTemplate - Currently selected template.
 * @param {Object[]} params.languageStandards - Selected language standards.
 * @param {Object[]} params.contentStandards - Selected content standards.
 * @param {Object|null} [params.curriculumSelection] - Align → Curriculum selection, so the
 *   follow-up generator knows which curriculum lesson the resource sits alongside.
 * @param {Object[]} params.selectedReadingLevels - Target reading levels.
 * @param {Object[]} params.selectedLanguages - Target output languages.
 * @param {Function} params.getTrackingContext - Returns Mixpanel tracking context.
 * @param {Function|null} params.findPossibleBookMatchForTopic - Book matching function.
 * @param {boolean} params.isCreating - Whether resource creation is in progress.
 * @param {Object} params.followUp - Follow-up flow hook return value.
 * @param {Function} params.setSelectedIndex - Setter for autocomplete selected index.
 * @param {Function} params.setSavedRequest - Setter for the saved request text (eagerly saved before API call).
 * @param {Function} params.getDefaultRequest - Returns a default request string.
 * @param {Function} params.handleCreate - Resource creation function (fallback when follow-ups fail).
 * @returns {{ isLoadingQuestions: boolean, handleSubmit: Function }}
 */
export const useSubmitFlow = ({
  query,
  setError,
  attachments,
  selectedBook,
  startChapter,
  endChapter,
  selectedTemplate,
  languageStandards,
  contentStandards,
  curriculumSelection = null,
  selectedReadingLevels,
  selectedLanguages,
  getTrackingContext,
  findPossibleBookMatchForTopic,
  isCreating,
  followUp,
  setSelectedIndex,
  setSavedRequest,
  getDefaultRequest,
  handleCreate,
  selectedMtssTier = null,
}) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { experimentFreeGenerate } = useFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const showSignInModal = useSignInModal();
  const { hideTutorial } = useTutorialContext();
  const { diffitUser } = useDiffitUserContext();
  const { showProjectQuotaError } = useError();
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  // Abort controller ref — cancels the previous inflight request on re-submit
  const abortRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const handleSubmit = useCallback(async () => {
    const effectiveRequest = query.trim() || getDefaultRequest();
    if (!effectiveRequest || isCreating || isLoadingQuestions) return;

    // Pre-submit sign-up gate. When experimentFreeGenerate is on, anonymous users
    // pass straight through and generate without signing in at all — the resulting
    // project has no owner until they sign in and claim it.
    if (isLoaded && !isSignedIn && !experimentFreeGenerate) {
      Mixpanel.track('Unlimited Generate Sign In Required');
      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        searchParams.set('prompt', trimmedQuery);
        setSearchParams(searchParams, { replace: true });
      }
      showSignInModal();
      return;
    }

    // Out of projects this week: say so now rather than after the follow-up questions.
    // The backend still enforces it at create if this copy of the quota is stale.
    const quota = diffitUser?.weekly_project_quota;
    if (isProjectQuotaExhausted(quota)) {
      showProjectQuotaError(quota);
      return;
    }

    // Validate uploads are ready
    const uploadError = checkUploadsReady(attachments);
    if (uploadError) {
      setError(uploadError);
      return;
    }

    // YouTube interception
    const trimmedQuery = query.trim();
    const youtubeUrl = findFirstYoutubeUrl(trimmedQuery, attachments.attachedLinks);
    if (youtubeUrl) {
      attachments.setYoutubeModalUrl(youtubeUrl);
      attachments.setShowYoutubeTranscriptModal(true);
      return;
    }

    // URL extraction + limit check
    const { urls: detectedUrls, error: urlError } = extractAndValidateUrls(
      trimmedQuery,
      attachments.attachedLinks
    );
    if (urlError) {
      setError(urlError);
      return;
    }

    // Every open callout (e.g. the post-onboarding profile-menu tip) is anchored to
    // the input screen the teacher is leaving, so close it once a submit commits.
    // The early returns above all keep the teacher on this screen, so they leave it up.
    hideTutorial({ location: 'submit' });

    Mixpanel.track('Unlimited Submit Clicked', {
      has_pdf: !!attachments.uploadedPdfURL,
      has_images: attachments.uploadedImageURLs.length > 0,
      image_count: attachments.uploadedImageURLs.length,
      has_book: !!selectedBook,
      auto_generated_request: !query.trim(),
      ...getTrackingContext(),
    });
    // Top of the Meta funnel. Deliberately here rather than earlier in the
    // handler: every return above keeps the teacher on the input screen (sign-in
    // gate, uploads still uploading, YouTube interception, too many links), so
    // this fires only once a generate actually commits.
    trackMetaCustomEvent('GenerateClicked');
    // Top of the ChatGPT funnel, on the same commit point as Meta's.
    trackChatGPTCustomEvent('generate_clicked');

    // Cancel any previous inflight request
    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    setIsLoadingQuestions(true);
    setSelectedIndex(-1);
    setError(null);
    setSavedRequest(effectiveRequest);

    // Book match detection (only when no book is explicitly selected)
    const detectedBookMatch = selectedBook
      ? null
      : detectBookMatch(effectiveRequest, findPossibleBookMatchForTopic);

    const sessionId = crypto.randomUUID();

    try {
      const result = await getFollowUpQuestions({
        request: effectiveRequest,
        previous_qa: [],
        generation_session_id: sessionId,
        uploaded_pdf_url: attachments.uploadedPdfURL || undefined,
        uploaded_image_urls:
          attachments.uploadedImageURLs.length > 0 ? attachments.uploadedImageURLs : undefined,
        urls: detectedUrls.length > 0 ? detectedUrls : undefined,
        detected_book_match: detectedBookMatch || undefined,
        selected_book: buildSelectedBook(selectedBook, startChapter, endChapter),
        context: buildContext({
          selectedTemplate,
          languageStandards,
          contentStandards,
          curriculumSelection,
          selectedReadingLevels,
          selectedLanguages,
        }),
        ...(selectedMtssTier ? { mtss_tier: selectedMtssTier } : {}),
        signal: abortController.signal,
      });

      // Don't act on stale responses if this request was aborted
      if (abortController.signal.aborted) return;

      followUp.startFollowUpFlow(result, effectiveRequest, sessionId);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;

      console.error('Error fetching follow-up questions:', err);

      // Out of free generations. The backend gates follow-up on the create
      // budget without spending from it, so we learn this *before* making the
      // user answer questions for a create that would only be rejected. Falling
      // through to the degradation branch below would do exactly that, so stop
      // here and ask them to sign in instead. Nothing is stashed for replay:
      // they haven't answered the questions yet, so there's no complete
      // generation to resume — the prompt goes back in the URL and they
      // re-submit, same as the pre-submit gate above.
      if (isAnonCreateLimit(err)) {
        Mixpanel.track('Unlimited Create Rate Limited', {
          transport: 'follow_up_gate',
          ...getTrackingContext(),
        });
        const trimmedQuery = query.trim();
        if (trimmedQuery) {
          searchParams.set('prompt', trimmedQuery);
          setSearchParams(searchParams, { replace: true });
        }
        showSignInModal({ headline: 'Sign in to continue' });
        return;
      }

      const errorData = err.response?.data;
      // Any remaining 429 is the endpoint's own per-minute burst limit (the
      // out-of-generations case returned above). It carries a
      // user_error_message, but showing it would block the user behind a retry
      // wall — route it to the graceful-degradation branch instead so they skip
      // follow-up questions and go straight to generation.
      if (errorData?.user_error_message && err.response?.status !== 429) {
        setError(errorData.user_error_message);
        Mixpanel.track('Unlimited URL Extraction Error', {
          urls: detectedUrls,
          error_name: errorData.name,
          error_message: errorData.user_error_message,
        });
      } else {
        // Graceful degradation: if follow-up questions fail for an unexpected
        // reason, skip them and proceed directly to resource creation so the
        // user isn't blocked.
        Mixpanel.track('Unlimited Submit Error', {
          error_message: err.message,
        });
        await handleCreate([]);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoadingQuestions(false);
      }
    }
  }, [
    query,
    getDefaultRequest,
    isCreating,
    isLoadingQuestions,
    isLoaded,
    isSignedIn,
    searchParams,
    setSearchParams,
    showSignInModal,
    attachments,
    selectedBook,
    startChapter,
    endChapter,
    selectedTemplate,
    languageStandards,
    contentStandards,
    curriculumSelection,
    selectedReadingLevels,
    selectedLanguages,
    getTrackingContext,
    findPossibleBookMatchForTopic,
    followUp,
    setError,
    setSelectedIndex,
    setSavedRequest,
    handleCreate,
    selectedMtssTier,
    hideTutorial,
    diffitUser?.weekly_project_quota,
    showProjectQuotaError,
  ]);

  return { isLoadingQuestions, handleSubmit };
};
